// Firebase Analytics utility.
//
// Dynamically imports Firebase SDK libraries to avoid bloating the bundle
// during mock auth dev sessions. Safe to call from anywhere.

import { authMode, getFirebaseAuth } from './firebase';
import type { Analytics } from 'firebase/analytics';

let analyticsPromise: Promise<Analytics | null> | null = null;

/**
 * Lazily initialize Firebase Analytics.
 * Returns null if auth mode is not 'firebase' or configuration is missing.
 */
export async function initAnalytics(): Promise<Analytics | null> {
  if (authMode !== 'firebase') return null;
  if (analyticsPromise) return analyticsPromise;

  analyticsPromise = (async () => {
    try {
      // Ensure the main Firebase App is initialized via getFirebaseAuth
      await getFirebaseAuth();

      const { getApps, getApp } = await import('firebase/app');
      const { getAnalytics, isSupported } = await import('firebase/analytics');

      // Analytics needs IndexedDB + cookies; unsupported in tests, some
      // private-browsing modes, and embedded webviews.
      if (!(await isSupported())) return null;

      const app = getApps().length > 0 ? getApp() : null;
      if (!app) return null;

      return getAnalytics(app);
    } catch (err) {
      console.warn('Failed to initialize Firebase Analytics:', err);
      return null;
    }
  })();

  return analyticsPromise;
}

/**
 * Log an event with custom parameters.
 */
export async function logAnalyticsEvent(eventName: string, params?: Record<string, any>) {
  if (authMode !== 'firebase') {
    if (import.meta.env.DEV) {
      console.log(`[Mock Analytics] Event logged: ${eventName}`, params);
    }
    return;
  }
  try {
    const analytics = await initAnalytics();
    if (analytics) {
      const { logEvent } = await import('firebase/analytics');
      logEvent(analytics, eventName, params);
    }
  } catch (err) {
    console.error('Failed to log event:', err);
  }
}

/**
 * Associate events with a specific user ID.
 */
export async function setAnalyticsUserId(userId: string | null) {
  if (authMode !== 'firebase') return;
  try {
    const analytics = await initAnalytics();
    if (analytics) {
      const { setUserId } = await import('firebase/analytics');
      setUserId(analytics, userId);
    }
  } catch (err) {
    console.error('Failed to set analytics user ID:', err);
  }
}

/**
 * Set custom user properties.
 */
export async function setAnalyticsUserProperties(properties: Record<string, any>) {
  if (authMode !== 'firebase') return;
  try {
    const analytics = await initAnalytics();
    if (analytics) {
      const { setUserProperties } = await import('firebase/analytics');
      setUserProperties(analytics, properties);
    }
  } catch (err) {
    console.error('Failed to set analytics user properties:', err);
  }
}
